import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
// Assuming you have a CSS file for styling
import '../../styles/MyProgress.css';

const MyProgress = () => {
  const [dietPlan, setDietPlan] = useState(null);
  const [loadingDiet, setLoadingDiet] = useState(true);
  const [errorDiet, setErrorDiet] = useState(null);
  // Set initial date to today
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [mealsProgress, setMealsProgress] = useState({});
  const [waterIntake, setWaterIntake] = useState('');
  const [sleepHours, setSleepHours] = useState('');
  const [mood, setMood] = useState('');
  const [dietScore, setDietScore] = useState('');
  const [notes, setNotes] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  // 'edit' for new/editing, 'view' for submitted, 'submitting', 'error'
  const [submissionStatus, setSubmissionStatus] = useState(null); 
  const [submittedProgress, setSubmittedProgress] = useState(null);

  // 1. Listen for Auth State Change
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // 2. Fetch Latest Diet Plan
  useEffect(() => {
    const fetchDietPlan = async () => {
      if (currentUser) {
        setLoadingDiet(true);
        setErrorDiet(null);
        try {
          // Query the latest diet plan for the user
          const dietPlansCollectionRef = collection(db, 'users', currentUser.uid, 'dietPlans');
          const q = query(dietPlansCollectionRef, orderBy('uploadedAt', 'desc'), limit(1));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const latestPlanDoc = querySnapshot.docs[0];
            setDietPlan(latestPlanDoc.data());
          } else {
            setDietPlan(null);
          }
        } catch (err) {
          console.error('Error fetching diet plan:', err);
          setErrorDiet('Failed to fetch diet plan.');
        } finally {
          setLoadingDiet(false);
        }
      }
    };

    fetchDietPlan();
  }, [currentUser]);

  // 3. Fetch Daily Progress for Selected Date
  useEffect(() => {
    const fetchProgressForDate = async () => {
      if (currentUser && selectedDate) {
        setSubmissionStatus(null); // Reset status before fetching
        try {
          // Document ID is the date (e.g., '2025-10-21')
          const progressDocRef = doc(db, 'progress', currentUser.uid, 'dailyProgress', selectedDate);
          const docSnap = await getDoc(progressDocRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            setSubmittedProgress(data);
            
            // Set states for viewing/potential editing
            setMealsProgress(data.meals || {});
            setWaterIntake(data.waterIntake || '');
            setSleepHours(data.sleepHours || '');
            setMood(data.mood || '');
            setDietScore(data.dietScore || '');
            setNotes(data.notes || '');
            
            setSubmissionStatus('view'); // Progress submitted, show summary
          } else {
            // No progress submitted for this date, reset inputs
            setSubmittedProgress(null);
            setMealsProgress({});
            setWaterIntake('');
            setSleepHours('');
            setMood('');
            setDietScore('');
            setNotes('');
            setSubmissionStatus('edit'); // Ready to edit/submit
          }
        } catch (error) {
          console.error('Error fetching progress for date:', error);
          setSubmissionStatus('error');
        }
      }
    };

    fetchProgressForDate();
  }, [currentUser, selectedDate]); // Re-fetch when user or date changes

  // Handlers
  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleMealCheck = (mealIndex) => {
    // Only allow checking/unchecking in 'edit' mode
    if (submissionStatus === 'edit') {
      setMealsProgress((prevProgress) => ({
        ...prevProgress,
        [mealIndex]: !prevProgress[mealIndex],
      }));
    }
  };

  const calculateProgress = () => {
    if (!dietPlan?.weeklyDietPlan) return 0;
    
    // Find the diet plan for the selected date
    const selectedDayPlan = dietPlan.weeklyDietPlan.find((day) => {
      // Ensure day.date is a valid date string before comparing
      const dayDate = day.date ? new Date(day.date).toISOString().split('T')[0] : null;
      return dayDate === selectedDate;
    });

    if (!selectedDayPlan || !selectedDayPlan.meals) return 0;

    const totalMeals = selectedDayPlan.meals.length;
    // Count meals that are checked (value is true)
    const checkedMeals = Object.values(mealsProgress).filter((checked) => checked).length;
    return totalMeals > 0 ? (checkedMeals / totalMeals) * 100 : 0;
  };

  const handleSubmitProgress = async (event) => {
    event.preventDefault();
    if (currentUser && selectedDate && submissionStatus === 'edit') {
      setSubmissionStatus('submitting');
      try {
        const progressData = {
          date: selectedDate,
          meals: mealsProgress,
          waterIntake,
          sleepHours,
          mood,
          dietScore,
          notes,
          submittedAt: new Date(), // Firestore Timestamp
        };
        // Save data to the document
        const progressDocRef = doc(db, 'progress', currentUser.uid, 'dailyProgress', selectedDate);
        await setDoc(progressDocRef, progressData);
        
        setSubmittedProgress(progressData); // Update state with submitted data
        setSubmissionStatus('view'); // Change to view mode
      } catch (error) {
        console.error('Error submitting progress:', error);
        setSubmissionStatus('error');
      }
    }
  };

  // 4. Render Logic (Loading/Errors/No Plan)
  if (loadingDiet) {
    return <div className="my-progress-container">Loading diet plan...</div>;
  }

  if (errorDiet) {
    return <div className="my-progress-container">Error: {errorDiet}</div>;
  }

  if (!dietPlan?.weeklyDietPlan) {
    return <div className="my-progress-container">No diet plan available.</div>;
  }

  // Find the meal plan for the selected date
  const selectedDayPlan = dietPlan.weeklyDietPlan.find((day) => {
    const dayDate = day.date ? new Date(day.date).toISOString().split('T')[0] : null;
    return dayDate === selectedDate;
  });

  if (!selectedDayPlan || !selectedDayPlan.meals) {
    return (
      <div className="my-progress-container">
        <h2>My Progress</h2>
        <label htmlFor="date">Select Date:</label>
        <input type="date" id="date" value={selectedDate} onChange={handleDateChange} />
        <p>No meal plan found for the selected date. Please select a date within your plan.</p>
      </div>
    );
  }

  const progressPercentage = calculateProgress();
  const formattedDate = new Date(selectedDate).toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  // 5. Main Component Return
  return (
    <div className="my-progress-container">
      <h2>My Progress</h2>
      
      <div className="date-selector">
        <label htmlFor="date">Select Date:</label>
        <input type="date" id="date" value={selectedDate} onChange={handleDateChange} />
      </div>

      <p className='date-display'>Viewing progress for: <strong>{formattedDate}</strong></p>
      
      {/* CONDITIONAL RENDERING: SHOW SUBMITTED PROGRESS (VIEW MODE)
        This section is what you specifically asked to ensure is shown/reflected.
      */}
      {submissionStatus === 'view' && submittedProgress ? (
        <div className="progress-summary">
          <h3>Daily Progress Summary</h3>
          <p className='submission-status-message'>✅ **Progress Submitted**</p>

          {/* Meal Checklist Reflection */}
          <div className="meals-summary">
            <h4>Meal Checklist:</h4>
            <ul>
              {selectedDayPlan.meals.map((meal, index) => (
                <li key={index} className={submittedProgress.meals[index] ? 'meal-completed' : 'meal-missed'}>
                  <input 
                    type="checkbox" 
                    checked={submittedProgress.meals[index] || false} 
                    readOnly // Checkboxes are read-only in view mode
                  />
                  <span>{meal || 'N/A'}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <hr />
          
          {/* Daily Stats Reflection */}
          <div className="daily-stats-summary">
            <h4>Daily Stats:</h4>
            <p><strong>Water Intake:</strong> {submittedProgress.waterIntake || 'N/A'}</p>
            <p><strong>Sleep Hours:</strong> {submittedProgress.sleepHours || 'N/A'}</p>
            <p><strong>Mood:</strong> {submittedProgress.mood || 'N/A'}</p>
            <p><strong>Diet Score:</strong> {submittedProgress.dietScore || 'N/A'}</p>
            {submittedProgress.notes && <p><strong>Notes:</strong> {submittedProgress.notes}</p>}
            
            {/* Submission timestamp is optional but good context */}
            {submittedProgress.submittedAt && submittedProgress.submittedAt.toDate && (
              <p className="submitted-at">
                Submitted At: {submittedProgress.submittedAt.toDate().toLocaleString()}
              </p>
            )}
          </div>

          <button onClick={() => setSubmissionStatus('edit')} className="edit-button">
            Edit/Update Progress
          </button>
        </div>
      ) : (
        /* CONDITIONAL RENDERING: SHOW PROGRESS SUBMISSION FORM (EDIT MODE)
        */
        <form onSubmit={handleSubmitProgress} className="progress-form">
          <h3>Track Your Progress</h3>
          <p className='submission-status-message'>*Progress not yet submitted for this date or ready for editing.*</p>
          
          {/* Meal Checklist Input */}
          <div className="meals-checklist">
            <h4>Meal Checklist:</h4>
            <ul>
              {selectedDayPlan.meals.map((meal, index) => (
                <li key={index}>
                  <input
                    type="checkbox"
                    checked={mealsProgress[index] || false}
                    onChange={() => handleMealCheck(index)}
                  />
                  {meal || 'N/A'}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Progress Bar and Cheer */}
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progressPercentage}%` }}>
              {progressPercentage.toFixed(0)}% Complete
            </div>
          </div>
          {progressPercentage === 100 && (
            <p className="cheerful-message">🎉 Great job! You've completed all your meals!</p>
          )}
          
          <hr />
          
          {/* Daily Stats Input */}
          <div className="additional-info">
            <h4>Daily Stats</h4>
            <div className="form-group">
              <label htmlFor="waterIntake">Water Intake (e.g., 8 glasses):</label>
              <input
                type="text"
                id="waterIntake"
                value={waterIntake}
                onChange={(e) => setWaterIntake(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="sleepHours">Sleep Hours:</label>
              <input
                type="number"
                id="sleepHours"
                value={sleepHours}
                onChange={(e) => setSleepHours(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="mood">Mood (e.g., Happy, Tired, Stressed):</label>
              <input type="text" id="mood" value={mood} onChange={(e) => setMood(e.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="dietScore">Diet Score (1=Poor, 5=Excellent):</label>
              <input
                type="number"
                id="dietScore"
                min="1"
                max="5"
                value={dietScore}
                onChange={(e) => setDietScore(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="notes">Additional Notes for Dietician:</label>
              <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {/* Submission Button & Status */}
          {submissionStatus === 'submitting' ? (
            <p>Submitting progress...</p>
          ) : (
            <button type="submit" className="submit-button">
              Submit Progress
            </button>
          )}
          {submissionStatus === 'error' && <p className="error-message">Failed to submit progress. Please try again.</p>}
        </form>
      )}
    </div>
  );
};

export default MyProgress;